import LazyLoad from "react-lazyload";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { accountUnBlockUser } from "../../redux/ApiSlice/AccountProfileSlice";

export default function ProfileSection({
    userId,
    handleFollowFun,
    handleStartChat,
    setActiveTab,
    showProfileModal,
    setShowProfileModal,
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [openPopup, setOpenPopup] = useState(false);
    const ProfileDetails = useSelector((state) => state.AccountProfile);
    const { otherUser_profile } = ProfileDetails || {};
    const {
        full_name,
        about_us,
        avatar,
        post_count,
        following_count,
        followers_count,
        is_private,
        is_following,
        portfolio,
        id,
        is_blocked,
        requested
    } = otherUser_profile || {}
    const [follow, setFollow] = useState(false);
    const [followRequested, setFollowRequested] = useState(false);
    const [showReasonDropdown, setShowReasonDropdown] = useState(false);
    const [isAboutExpanded, setIsAboutExpanded] = useState(false);
    const userRef = useRef(null);

    const handleNavigate = (value) => {
        if ((is_private && is_following) || !is_private) {
            setActiveTab(value)
        }
    };

    const path = location?.pathname?.split("/");
    const label = path[path.length - 2];

    const handleClickOutside = () => {
        setOpenPopup(false);
    }

    useOutsideClick(userRef, handleClickOutside);

    const handleUnBlockUser = async () => {
        try {
            const result = await dispatch(accountUnBlockUser(id));
            if (
                result?.type === "accountUnBlockUser/fulfilled" &&
                result?.payload?.message?.toLowerCase().includes("unblocked")
            ) {
                setOpenPopup(false);
            }
        } catch (error) {
            console.error("Error unblocking user:", error);
        }
    }

    const handleProfileClick = () => {
        setShowProfileModal(true);
    }

    const truncateText = (text, maxLength = 100) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    }

    const handleAboutToggle = () => {
        setIsAboutExpanded(!isAboutExpanded);
    }

    useEffect(() => {
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape' && showProfileModal) {
                setShowProfileModal(false);
            }
        };

        const handleClickOutside = (event) => {
            if (showReasonDropdown && !event.target.closest('.custom-dropdown')) {
                setShowReasonDropdown(false);
            }
        };

        if (showProfileModal) {
            document.addEventListener('keydown', handleEscapeKey);
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';

            return () => {
                document.removeEventListener('keydown', handleEscapeKey);
                document.body.style.overflow = originalOverflow || '';
            };
        }

        if (showReasonDropdown) {
            document.addEventListener('click', handleClickOutside);
        }

        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showProfileModal, setShowProfileModal, showReasonDropdown]);

    useEffect(() => {
        setFollow(is_following);
        setFollowRequested(requested);
    }, [is_following, requested])

    return (
        <div className="relative">
            <div className="px-2 mt-2">
                <div className="flex items-center gap-3">
                    <div className="shrink-0">
                        <LazyLoad height={200} offset={100}>
                            <img
                                src={avatar}
                                alt={full_name}
                                onClick={handleProfileClick}
                                className="h-[4rem] w-[4rem] rounded-full object-cover border border-gray-400 dark:border-gray-800 cursor-pointer hover:opacity-80 transition-opacity duration-200"
                            />
                        </LazyLoad>
                    </div>
                    <div className="flex flex-col gap-1 pt-1 min-w-0">
                        <p className="text-black dark:text-white font-bold text-[15px] line-clamp-1">
                            {full_name}
                        </p>
                        {!is_blocked && (
                            <div className="flex items-center gap-3 text-[10px] font-medium text-black dark:text-white">
                                <span onClick={() => handleNavigate("posts")} className="cursor-pointer">
                                    <span className="font-bold">{post_count}</span> Posts
                                </span>
                                <span onClick={() => navigate(`/user/followers/${userId}`)} className="cursor-pointer">
                                    <span className="font-bold">{followers_count}</span> Followers
                                </span>
                                <span onClick={() => navigate(`/user/following/${userId}`)} className="cursor-pointer">
                                    <span className="font-bold">{following_count}</span> Following
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {portfolio && (
                    <a
                        target="_blank"
                        href={portfolio}
                        rel="noopener noreferrer"
                        className="text-black dark:text-white hover:text-blue-700 cursor-pointer text-[10px] leading-[14px] mt-2 line-clamp-1"
                    >
                        {portfolio}
                    </a>
                )}

                <div className="text-[#000000] dark:text-white font-medium text-[11px] leading-[15px] mt-0.5">
                    {about_us && about_us.length > 100 ? (
                        <>
                            <span className={isAboutExpanded ? "" : "line-clamp-2"}>
                                {isAboutExpanded ? about_us : truncateText(about_us, 100)}
                            </span>
                            <button
                                onClick={handleAboutToggle}
                                className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 ml-1 text-[10px] font-medium cursor-pointer transition-colors duration-200"
                            >
                                {isAboutExpanded ? "Show less" : "Show more"}
                            </button>
                        </>
                    ) : (
                        <span className="line-clamp-2">{about_us}</span>
                    )}
                </div>
            </div>

            <div className="px-2 py-1 mt-2 flex items-center justify-center gap-x-2">
                {!is_blocked ?
                    <button
                        onClick={handleFollowFun}
                        className={`rounded-lg font-semibold w-full text-[12px] py-1.5 ${(is_private && follow && followRequested)
                            ? "bg-gray-500 hover:bg-gray-600"
                            : (follow && !followRequested) || (!is_private && follow)
                                ? "bg-[#BDE6FF] hover:bg-[#A3D4FF]" : "bg-[#BDE6FF] hover:bg-[#A3D4FF]"
                            } text-black`}
                    >
                        {
                            (is_private && follow && followRequested)
                                ? "Requested"
                                : (follow && !followRequested) || (!is_private && follow)
                                    ? "Following" : "Follow"
                        }
                    </button>
                    :
                    <button
                        onClick={handleUnBlockUser}
                        className={`rounded-lg font-semibold w-full text-[12px] py-1.5 ${follow
                            ? "bg-[#BDE6FF] hover:bg-[#A3D4FF]"
                            : "bg-[#BDE6FF] hover:bg-[#A3D4FF]"
                            } text-black`}
                    >
                        Unblock
                    </button>
                }

                {!is_blocked && !is_private &&
                    <button
                        onClick={() => handleStartChat()}
                        className="rounded-lg w-full text-[12px] font-semibold py-1.5 bg-white text-black border border-gray-300"
                    >
                        Message
                    </button>
                }
            </div>
        </div>
    );
}
